import { useState, useCallback, useMemo } from 'react';
import type {
  EventFilters,
  FilterCategory,
  FilterPillData,
  OwnerOption,
  PlannerEventRow,
  PlannerEventStatus,
} from '@/types/planner.types';
import { EMPTY_FILTERS, UNASSIGNED_OWNER_ID } from '@/types/planner.types';
import { formatDate } from '@/utils/dateFormat';

const parseDateFromString = (dateStr: string): Date | null => {
  const parts = dateStr.split(/[-T ]/);
  if (parts.length < 3) return null;
  const year = parseInt(parts[0]!, 10);
  const month = parseInt(parts[1]!, 10) - 1;
  const day = parseInt(parts[2]!, 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month, day);
};

const UNASSIGNED_OWNER_NAME = 'unassigned';

const matchesOwners = (event: PlannerEventRow, owners: string[]): boolean => {
  if (owners.length === 0) return true;
  const ownerEmail = event.owner.email?.toLowerCase();
  const ownerName = event.owner.name?.toLowerCase();
  const unassignedId = UNASSIGNED_OWNER_ID.toLowerCase();
  return owners.some((rawId) => {
    const id = rawId.trim().toLowerCase();
    if (id === unassignedId) {
      return !ownerEmail && (!ownerName || ownerName === UNASSIGNED_OWNER_NAME);
    }
    if (ownerEmail && ownerEmail === id) return true;
    if (ownerName && ownerName === id) return true;
    return false;
  });
};

const matchesStatuses = (event: PlannerEventRow, statuses: EventFilters['statuses']): boolean => {
  if (statuses.length === 0) return true;
  return statuses.some((s) => {
    if (s === 'Cancelled') return event.status === 'Cancelled';
    return event.status === s;
  });
};

const matchesBuildings = (event: PlannerEventRow, buildings: string[]): boolean => {
  if (buildings.length === 0) return true;
  return buildings.includes(event.buildingId);
};

const matchesDateRange = (
  dateStr: string,
  startDate: Date | null,
  endDate: Date | null
): boolean => {
  if (!startDate && !endDate) return true;
  const eventDate = parseDateFromString(dateStr);
  if (!eventDate) return true;
  if (startDate && eventDate < startDate) return false;
  if (endDate) {
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    if (eventDate > endOfDay) return false;
  }
  return true;
};

const buildDatePillValue = (
  startDate: Date | null,
  endDate: Date | null,
  fromLabel: string,
  untilLabel: string
): string | null => {
  if (startDate && endDate) {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }
  if (startDate) {
    return `${fromLabel} ${formatDate(startDate)}`;
  }
  if (endDate) {
    return `${untilLabel} ${formatDate(endDate)}`;
  }
  return null;
};

export interface UseEventFiltersReturn {
  filters: EventFilters;
  applyFilters: (filters: EventFilters) => void;
  removeFilter: (category: FilterCategory) => void;
  updateFilter: (category: FilterCategory, values: string[]) => void;
  clearAllFilters: () => void;
  hasActiveFilters: boolean;
  filterPills: FilterPillData[];
  filterEvents: (events: PlannerEventRow[]) => PlannerEventRow[];
}

export interface UseEventFiltersOptions {
  availableOwners?: OwnerOption[];
  availableBuildings?: OwnerOption[];
  buildingLabel?: string;
}

export const useEventFilters = (options: UseEventFiltersOptions = {}): UseEventFiltersReturn => {
  const { availableOwners = [], availableBuildings = [], buildingLabel = 'Building:' } = options;
  const [filters, setFilters] = useState<EventFilters>(EMPTY_FILTERS);

  const applyFilters = useCallback((newFilters: EventFilters) => {
    setFilters(newFilters);
  }, []);

  const removeFilter = useCallback((category: FilterCategory) => {
    setFilters((prev) => {
      switch (category) {
        case 'owner':
          return { ...prev, owners: [] };
        case 'status':
          return { ...prev, statuses: [] };
        case 'building':
          return { ...prev, buildings: [] };
        case 'submittedDate':
          return { ...prev, submissionStartDate: null, submissionEndDate: null };
        case 'eventDate':
          return { ...prev, eventStartDate: null, eventEndDate: null };
        default:
          return prev;
      }
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
  }, []);

  const updateFilter = useCallback((category: FilterCategory, values: string[]) => {
    setFilters((prev) => {
      switch (category) {
        case 'owner':
          return { ...prev, owners: values };
        case 'status': {
          const statusValues: PlannerEventStatus[] = values.map((v) => v as PlannerEventStatus);
          return { ...prev, statuses: statusValues };
        }
        case 'building':
          return { ...prev, buildings: values };
        default:
          return prev;
      }
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.owners.length > 0 ||
      filters.statuses.length > 0 ||
      filters.buildings.length > 0 ||
      filters.submissionStartDate !== null ||
      filters.submissionEndDate !== null ||
      filters.eventStartDate !== null ||
      filters.eventEndDate !== null
    );
  }, [filters]);

  const filterPills = useMemo((): FilterPillData[] => {
    const pills: FilterPillData[] = [];

    if (filters.owners.length > 0) {
      const ownerMap = new Map(availableOwners.map((o) => [o.id, o.displayName]));
      const displayValues = filters.owners.map((id) => ownerMap.get(id) ?? id);
      pills.push({
        category: 'owner',
        label: 'Owner:',
        displayValue: displayValues.join(', '),
      });
    }

    if (filters.statuses.length > 0) {
      pills.push({
        category: 'status',
        label: 'Status:',
        displayValue: filters.statuses.join(', '),
      });
    }

    if (filters.buildings.length > 0) {
      const buildingMap = new Map(availableBuildings.map((b) => [b.id, b.displayName]));
      pills.push({
        category: 'building',
        label: buildingLabel,
        displayValue: filters.buildings.map((id) => buildingMap.get(id) ?? id).join(', '),
      });
    }

    const submittedDateValue = buildDatePillValue(
      filters.submissionStartDate,
      filters.submissionEndDate,
      'From',
      'Until'
    );
    if (submittedDateValue) {
      pills.push({
        category: 'submittedDate',
        label: 'Submitted Date:',
        displayValue: submittedDateValue,
      });
    }

    const eventDateValue = buildDatePillValue(
      filters.eventStartDate,
      filters.eventEndDate,
      'From',
      'Until'
    );
    if (eventDateValue) {
      pills.push({
        category: 'eventDate',
        label: 'Event Date:',
        displayValue: eventDateValue,
      });
    }

    return pills;
  }, [filters, availableOwners, availableBuildings, buildingLabel]);

  const filterEvents = useCallback(
    (events: PlannerEventRow[]): PlannerEventRow[] => {
      if (!hasActiveFilters) return events;
      return events.filter(
        (event) =>
          matchesOwners(event, filters.owners) &&
          matchesStatuses(event, filters.statuses) &&
          matchesBuildings(event, filters.buildings) &&
          matchesDateRange(
            event.submittedDate,
            filters.submissionStartDate,
            filters.submissionEndDate
          ) &&
          matchesDateRange(event.eventDate, filters.eventStartDate, filters.eventEndDate)
      );
    },
    [filters, hasActiveFilters]
  );

  return {
    filters,
    applyFilters,
    removeFilter,
    updateFilter,
    clearAllFilters,
    hasActiveFilters,
    filterPills,
    filterEvents,
  };
};
